import type { FilterSchema } from '@/shared/components/filter/validation'
import type { TFunction } from 'i18next'
import type { WidgetPeriods } from '../types'

import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { filter, map } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	buildDriverRows,
	DemandDriversChart,
} from '@/features/mid-term-forecast'

import { useAiBIAnalyticQuery, useMetricsQuery } from '@/entities/bi'

import { ampli } from '@/shared/lib/ampli'

import { AiDashboardFilter } from '../../Filter'
import { getDefautFields } from '../../MainDashboard/useFilterFiledsConfig'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'
import WidgetLoadingState from '../../WidgetLoadingState'

const translateMetric = (metric: string, t: TFunction) => t(`metric.${metric}`)

export default function SalesFactorsImpactDynamicWidget({
	commonFilters,
	commonPeriods,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const { params, onChangeParams } = useWidgetParams()
	const {
		data: metrics,
		isLoading: isMetricsLoading,
		isError: isMetricsError,
	} = useMetricsQuery<string[]>({
		select: (data) => map('name', filter({ type: 'ai_importance' }, data)),
	})
	const commonQueryParams = {
		label: 'SalesFactorsImpactDynamic',
		...(commonPeriods?.main || {
			start_date: null,
			end_date: null,
		}),
		skip_dates: true,
		metrics: metrics as string[],
		dimensions: [],
		date_aggregation: 'date_key' as const,
		filters: [...commonFilters, ...(params.filters ?? [])],
	}
	const {
		data: biAnalytic,
		isLoading: isBiAnalyticLoading,
		isError: isBiAnalyticError,
	} = useAiBIAnalyticQuery(commonQueryParams, {
		enabled: !!metrics?.length,
		select: (data) => buildDriverRows(data.meta, data.data),
	})
	const labels = Object.fromEntries(
		(metrics ?? []).map((metric) => [metric, translateMetric(metric, t)])
	)
	const isLoading = isMetricsLoading || isBiAnalyticLoading
	const isError = isMetricsError || isBiAnalyticError

	return (
		<Widget className='relative flex flex-auto flex-col'>
			<WidgetLoadingState isTinted={isLoading} />
			<WidgetErrorState isTinted={isError} />
			<WidgetHeader title={t('widget.sales_factors_impact_dynamic.title')}>
				<AiDashboardFilter
					filters={params.filters ?? []}
					fields={getDefautFields(t).filter(
						({ value }) => value !== 'pricing_campaign_engine'
					)}
					onChange={(filters) => {
						ampli.dashboardBiApplyFilterSalesFactorsImpactDynamic()
						onChangeParams({ ...params, filters })
					}}
				/>
			</WidgetHeader>
			<WidgetContent className='space-y-3 pb-0'>
				{!biAnalytic?.rows.length && !isLoading ? (
					<WidgetEmptyState />
				) : (
					<DemandDriversChart
						rows={biAnalytic?.rows ?? []}
						drivers={biAnalytic?.drivers ?? []}
						labels={labels}
						height={390}
					/>
				)}
			</WidgetContent>
		</Widget>
	)
}
